/**
 * adminApi.js — client for analytics endpoints.
 * Uses the existing Axios instance (JWT auto-refresh).
 *
 * Each function returns the `data` shape from the API contract,
 * or an empty fallback if the backend is unavailable (404/5xx).
 *
 * Use makeAnalyticsApi(base) to create a set of wrappers bound to a
 * different base path (e.g. '/marketing/site-analytics' or '/seo/analytics').
 * The named exports below default to '/admin/analytics' and are preserved
 * unchanged so existing admin call sites need no modification.
 */

import api from '../utils/api';
import { subDays, formatISO } from 'date-fns';

// ─── Default date range helpers ───────────────────────────────────────────────

function defaultFrom() {
  return formatISO(subDays(new Date(), 7), { representation: 'date' });
}

function defaultTo() {
  return formatISO(new Date(), { representation: 'date' });
}

function baseParams(overrides = {}) {
  return {
    from: defaultFrom(),
    to: defaultTo(),
    ...overrides,
  };
}

// ─── Empty fallbacks (used when backend is not yet live) ──────────────────────

const EMPTY_OVERVIEW = {
  kpis: { visits: 0, uniques: 0, sessions: 0, avgDwellMs: 0, bounceRate: 0, activeNow: 0 },
  trend: [],
  topPages: [],
  topReferrers: [],
  sources: { direct: 0, organic: 0, referral: 0, campaign: 0 },
};

const EMPTY_AUDIENCE = { rows: [], geo: [] };

const EMPTY_BEHAVIOR_PAGES = { rows: [] };

const EMPTY_BEHAVIOR_FLOW = { nodes: [], links: [] };

const EMPTY_ACQUISITION = { referrers: [], utm: [] };

const EMPTY_CONVERSIONS = {
  funnel: { id: '', name: '', steps: [] },
  revenuePaise: 0,
};

const EMPTY_REALTIME = {
  activeCount: 0,
  byPath: [],
  byCountry: [],
  recent: [],
};

const EMPTY_USERS = { rows: [], total: 0 };

const EMPTY_TIMELINE = { items: [] };

const EMPTY_EVENTS = { rows: [] };

// ─── Safe fetch wrapper ───────────────────────────────────────────────────────

async function safeFetch(fn, fallback) {
  try {
    const res = await fn();
    return res?.data?.data ?? fallback;
  } catch (err) {
    const status = err?.response?.status;
    // Return empty shape for any error (404 = not yet live, 5xx = backend down)
    if (!status || status >= 400) return fallback;
    return fallback;
  }
}

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * makeAnalyticsApi(base)
 *
 * Returns an object containing all analytics API wrapper functions bound to
 * the given base path. Use this to create per-role API instances:
 *
 *   const api = makeAnalyticsApi('/marketing/site-analytics');
 *   api.getOverview({ from, to });
 *
 * @param {string} base - Base path prefix (no trailing slash).
 *   Default: '/admin/analytics'
 */
export function makeAnalyticsApi(base = '/admin/analytics') {
  return {
    getOverview(params = {}) {
      return safeFetch(
        () => api.get(`${base}/overview`, { params: baseParams(params) }),
        EMPTY_OVERVIEW,
      );
    },

    getAudience(params = {}) {
      return safeFetch(
        () => api.get(`${base}/audience`, { params: baseParams(params) }),
        EMPTY_AUDIENCE,
      );
    },

    getBehaviorPages(params = {}) {
      return safeFetch(
        () => api.get(`${base}/behavior/pages`, { params: baseParams({ limit: 50, ...params }) }),
        EMPTY_BEHAVIOR_PAGES,
      );
    },

    getBehaviorFlow(params = {}) {
      return safeFetch(
        () => api.get(`${base}/behavior/flow`, {
          params: baseParams({ entry: '/', depth: 5, ...params }),
        }),
        EMPTY_BEHAVIOR_FLOW,
      );
    },

    getAcquisition(params = {}) {
      return safeFetch(
        () => api.get(`${base}/acquisition`, { params: baseParams(params) }),
        EMPTY_ACQUISITION,
      );
    },

    getConversions(params = {}) {
      return safeFetch(
        () => api.get(`${base}/conversions`, { params: baseParams(params) }),
        EMPTY_CONVERSIONS,
      );
    },

    getRealtime() {
      return safeFetch(
        () => api.get(`${base}/realtime`),
        EMPTY_REALTIME,
      );
    },

    getUsers(params = {}) {
      return safeFetch(
        () => api.get(`${base}/users`, {
          params: { limit: 50, offset: 0, ...params },
        }),
        EMPTY_USERS,
      );
    },

    getUserTimeline(userId, params = {}) {
      return safeFetch(
        () => api.get(`${base}/users/${userId}/timeline`, {
          params: baseParams(params),
        }),
        EMPTY_TIMELINE,
      );
    },

    getEvents(params = {}) {
      return safeFetch(
        () => api.get(`${base}/events`, {
          params: { limit: 100, ...params },
        }),
        EMPTY_EVENTS,
      );
    },
  };
}

// ─── Named exports (admin default) ───────────────────────────────────────────
// These preserve the existing import { getOverview, ... } from 'adminApi' API
// so all current admin call sites continue to work with zero changes.

const _adminApi = makeAnalyticsApi('/admin/analytics');

/**
 * getOverview(params) — KPIs, trend, top pages, top referrers, sources.
 * @param {Object} params — { from, to, includeInternalRoles, includeBots }
 */
export const getOverview = _adminApi.getOverview.bind(_adminApi);

/**
 * getAudience(params) — dimension breakdown rows + geo array.
 * @param {Object} params — { from, to, dimension, newReturning, ... }
 */
export const getAudience = _adminApi.getAudience.bind(_adminApi);

/**
 * getBehaviorPages(params) — page performance table.
 * @param {Object} params — { from, to, limit }
 */
export const getBehaviorPages = _adminApi.getBehaviorPages.bind(_adminApi);

/**
 * getBehaviorFlow(params) — Sankey flow data.
 * @param {Object} params — { from, to, entry, depth }
 */
export const getBehaviorFlow = _adminApi.getBehaviorFlow.bind(_adminApi);

/**
 * getAcquisition(params) — referrers + UTM table.
 * @param {Object} params — { from, to, ... }
 */
export const getAcquisition = _adminApi.getAcquisition.bind(_adminApi);

/**
 * getConversions(params) — funnel steps + revenue.
 * @param {Object} params — { from, to, funnel }
 */
export const getConversions = _adminApi.getConversions.bind(_adminApi);

/**
 * getRealtime() — active count, by-path, by-country, recent feed.
 * No date params — always current.
 */
export const getRealtime = _adminApi.getRealtime.bind(_adminApi);

/**
 * getUsers(params) — paginated user table.
 * @param {Object} params — { search, limit, offset }
 */
export const getUsers = _adminApi.getUsers.bind(_adminApi);

/**
 * getUserTimeline(userId, params) — chronological event list for a user.
 * @param {string} userId
 * @param {Object} params — { from, to }
 */
export const getUserTimeline = _adminApi.getUserTimeline.bind(_adminApi);

/**
 * getEvents(params) — raw event rows.
 * @param {Object} params — { name, limit }
 */
export const getEvents = _adminApi.getEvents.bind(_adminApi);
